import { useMemo, useState } from 'react';
import _ from 'lodash';
import { plural, t, Trans } from '@lingui/macro';
import { Box, Button } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpTextField from '../../form/NgpTextField';
import { AGENT_BULK_XMS_HOST_FIELD, AGENT_STATUS_RUNNING } from './AgentConstants';
import { AUTO_COMPLETE_DEFAULT, MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useDataService } from '../../../services/ApiDataService';
import useUiAlerts, { createAlertConfig, WARNING } from '../../../stores/AlertsStore';

const formId = 'agentUrlForm';
const defaultFormValues = {};
const titleAndLabel = t`nPoint Server URL`;
const endsWithErrorText = t`The nPoint Management URL must end with /nPoint`;
const agentSelectionUrl = '/query/admin/agents/findSelected';
const agentVoipUsageUrl = '/ipm/v1/admin/agents/checkVoipTestUsage';
const MAX_AGENT_PER_PLM = 5000;
const allowedMoveStatuses = [AGENT_STATUS_RUNNING];

export default function AgentListBulkUrl({
    onConfirm,
    disabled = false,
    tableSelection,
    tableSearch = '',
    searchFields,
}) {
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues: defaultFormValues });
    const [showBulkUrlDialog, setShowBulkUrlDialog] = useState(false);
    const [confirmDisabled, setConfirmDisabled] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const dataService = useDataService();
    const [, uiAlertActions] = useUiAlerts();

    const { handleSubmit, reset, setError } = methods;

    const handleAgentUrl = async () => {
        setIsProcessing(true);

        try {
            // Validate that there is a selection
            if (!tableSelection || (!tableSelection.inverted && _.isEmpty(tableSelection.selection))) {
                uiAlertActions.createAlert(createAlertConfig(WARNING, t`Please select at least one nPoint to move`));
                return;
            }

            // Get selected agents
            const requestParams = {
                ...(tableSearch && { search: tableSearch }),
                limit: MAX_AGENT_PER_PLM,
                searchFields,
            };

            const response = await dataService.post(agentSelectionUrl, tableSelection, requestParams);
            const agents = response?.data || [];

            if (agents.length === 0) {
                uiAlertActions.createAlert(createAlertConfig(WARNING, t`No nPoints selected`));
                return;
            }

            // Check if any agent has invalid status
            const invalidStatusAgents = _.filter(agents, (agent) => {
                return !allowedMoveStatuses.includes(agent.status);
            });

            if (invalidStatusAgents.length > 0) {
                const agentNames = _.map(invalidStatusAgents, 'name').join(', ');
                uiAlertActions.createAlert(
                    createAlertConfig(
                        WARNING,
                        plural(invalidStatusAgents.length, {
                            one: `The following nPoint must be in Running status to be moved: ${agentNames}`,
                            other: `The following nPoints must be in Running status to be moved: ${agentNames}`,
                        })
                    )
                );
                return;
            }

            // Check if any agent has invalid software version (must be >= 3.16.0)
            const invalidVersionAgents = _.filter(agents, (agent) => {
                const softwareVersion = agent?.version?.softwareVersion;
                if (!softwareVersion) {
                    return true; // Agent without version info is invalid
                }

                // Parse version string (e.g., "3.16.0-0.203.0-970033230")
                const versionParts = softwareVersion.split('-')[0].split('.');
                const majorVersion = parseInt(versionParts[0], 10);
                const minorVersion = parseInt(versionParts[1], 10);

                // Check: versions below 3 are invalid, and 3.x requires minor >= 16
                return majorVersion < 3 || (majorVersion === 3 && minorVersion < 16);
            });

            if (invalidVersionAgents.length > 0) {
                const agentNames = _.map(invalidVersionAgents, 'name').join(', ');
                uiAlertActions.createAlert(
                    createAlertConfig(
                        WARNING,
                        plural(invalidVersionAgents.length, {
                            one: `The following nPoint must have software version 3.16.0 or higher to be moved: ${agentNames}`,
                            other: `The following nPoints must have software version 3.16.0 or higher to be moved: ${agentNames}`,
                        })
                    )
                );
                return;
            }

            // Filter agents with VoIP configured (voipMode not 'N')
            const voipAgents = _.filter(agents, (agent) => {
                const { voipMode } = agent;
                return voipMode && voipMode !== 'N';
            });

            // Check if VoIP agents are used by VoIP tests
            if (!_.isEmpty(voipAgents)) {
                const voipAgentIds = _.map(voipAgents, '_id');
                const usageResponse = await dataService.post(agentVoipUsageUrl, { agentIds: voipAgentIds });

                // Backend returns { hasUsage: boolean }
                const hasUsage = usageResponse?.hasUsage || false;
                if (hasUsage) {
                    const voipAgentNames = _.map(voipAgents, 'name').join(', ');
                    uiAlertActions.createAlert(
                        createAlertConfig(
                            WARNING,
                            plural(voipAgents.length, {
                                one: `The following nPoint is currently being used by a VoIP test and cannot be moved: ${voipAgentNames}`,
                                other: `The following nPoints are currently being used by VoIP tests and cannot be moved: ${voipAgentNames}`,
                            })
                        )
                    );
                    return;
                }
            }

            // All validations passed, open the dialog
            setShowBulkUrlDialog(true);
            setConfirmDisabled(true);
        } catch (error) {
            uiAlertActions.createAlert(
                createAlertConfig(WARNING, t`Failed to validate nPoint selection. Please try again.`)
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCleanup = () => {
        reset(defaultFormValues);
        setShowBulkUrlDialog(false);
    };
    const onChangeUrl = (value) => {
        const urlValue = typeof value === 'string' ? value : value?.target?.value || '';
        const shouldDisable = urlValue.trim().length === 0;

        setConfirmDisabled(shouldDisable);
    };

    const handleFormSubmit = (dataToSave = {}) => {
        const mustEndWith = '/npoint';

        // Check URL ending
        const urlLower = _.toLower(dataToSave[AGENT_BULK_XMS_HOST_FIELD]);
        const endsWithCheck = _.endsWith(urlLower, mustEndWith);

        if (!endsWithCheck) {
            setError(AGENT_BULK_XMS_HOST_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: endsWithErrorText,
            });
            return;
        }
        setConfirmDisabled(true);

        return Promise.resolve()
            .then(() => onConfirm(dataToSave))
            .then(handleCleanup);
    };
    const serverUrlValidationRules = useMemo(() => {
        const validateServerUrl = (value) => {
            if (!value) return true;

            if (!value.toLowerCase().startsWith('https://')) {
                return 'URL must start with https://';
            }

            if (!value.toLowerCase().endsWith('/npoint')) {
                return 'URL must end with /npoint';
            }

            let hostname = '';

            // remove https://
            const urlWithoutProtocol = value.slice(8);

            // remove /npoint
            if (urlWithoutProtocol.endsWith('/npoint')) {
                hostname = urlWithoutProtocol.slice(0, -7);
            } else {
                return 'URL must end with /npoint';
            }

            if (!hostname || hostname.trim() === '') {
                return 'Hostname cannot be empty';
            }

            // IPv4 Matches: 0.0.0.0 to 255.255.255.255
            const ipv4Regex =
                /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

            // IPv6 address validation regex - supports multiple formats:
            // 1. Full format: 8 hexadecimal groups separated by colons (e.g., 2001:0db8:85a3:0000:0000:8a2e:0370:7334)
            // 2. Compressed format with :: at end (e.g., 2001:db8:85a3::)
            // 3. Compressed format with :: in middle (e.g., 2001:db8::8a2e:370:7334)
            // 4. Compressed format with :: at beginning (e.g., ::1 for localhost)
            // 5. Various other compression scenarios according to RFC 4291
            const ipv6Regex = new RegExp(
                '^(' +
                    '([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|' + // Full format: xxxx:xxxx:xxxx:xxxx:xxxx:xxxx:xxxx:xxxx
                    '([0-9a-fA-F]{1,4}:){1,7}:|' + // Compressed with :: at end
                    '([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|' + // :: in position 7
                    '([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|' + // :: in position 6
                    '([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|' + // :: in position 5
                    '([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|' + // :: in position 4
                    '([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|' + // :: in position 3
                    '[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|' + // :: in position 2
                    ':((:[0-9a-fA-F]{1,4}){1,7}|:)' + // :: at beginning
                    ')$'
            );
            // domain name
            const domainRegex =
                /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,45}[a-zA-Z0-9])?|localhost)(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,45}[a-zA-Z0-9])?)*$|^localhost$/;

            const isValidIPv4 = ipv4Regex.test(hostname);

            let isValidIPv6 = false;
            if (hostname.startsWith('[') && hostname.endsWith(']')) {
                const ipv6Part = hostname.slice(1, -1);
                isValidIPv6 = ipv6Regex.test(ipv6Part);
            } else {
                isValidIPv6 = ipv6Regex.test(hostname);
            }

            const isValidDomain = domainRegex.test(hostname);

            if (!isValidIPv4 && !isValidIPv6 && !isValidDomain) {
                return 'Invalid hostname format. Must be IPv4, IPv6, domain name, or localhost';
            }

            return true;
        };

        return {
            serverUrl: {
                validate: {
                    serverUrl: validateServerUrl,
                },
            },
        };
    }, []);

    return (
        <>
            <Button onClick={handleAgentUrl} disabled={disabled || isProcessing}>
                <Trans>Move</Trans>
            </Button>
            <NgpTwoButtonDialog
                title={<Trans>nPoint Server URL</Trans>}
                confirmButtonText={<Trans>Move</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
                confirmButtonDisabled={confirmDisabled}
                onCloseFunc={handleCleanup}
                open={showBulkUrlDialog}
                setOpen={setShowBulkUrlDialog}
                onConfirm={handleSubmit(handleFormSubmit)}
                disableBackdropClick
                callerWillCloseOnConfirm
            >
                <Box width={600}>
                    <FormProvider {...methods}>
                        <NgpForm
                            formId={formId}
                            initialized
                            loading={false}
                            showSave={false}
                            formPadding={false}
                            showRequiredFieldsTip={false}
                            submitFunction={handleFormSubmit}
                        >
                            <FormSection>
                                <NgpTextField
                                    fieldName={AGENT_BULK_XMS_HOST_FIELD}
                                    label={titleAndLabel}
                                    onChangeFunc={onChangeUrl}
                                    required
                                    inputProps={{
                                        size: 60,
                                        maxLength: 255,
                                        autoComplete: AUTO_COMPLETE_DEFAULT,
                                    }}
                                    fieldRules={serverUrlValidationRules.serverUrl}
                                    helpTipText={
                                        <Trans>
                                            The URL to the nPoint server, typically HTTPS. An example would be
                                            https://localhost/npoint
                                        </Trans>
                                    }
                                />
                            </FormSection>
                        </NgpForm>
                    </FormProvider>
                </Box>
            </NgpTwoButtonDialog>
        </>
    );
}
